import React, { useState, useRef, KeyboardEvent } from 'react';

import {
  ListItemProps,
  ListItemText,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Collapse,
  Button,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTrackUserEvent } from '../../hooks/api/useTrackUserEvent';
import { useAuthContext } from '../../contexts/AuthContext';
import { navigationButtonStyles } from './sidebarStyles';

type SidebarNavLinkItemProps = ListItemProps & {
  depth: number;
  href: string;
  icon?: React.ComponentType;
  badge?: string;
  open?: boolean;
  title: string;
  target?: string;
  eventCode?: string;
};

const SidebarNavLinkItem: React.FC<SidebarNavLinkItemProps> = (props) => {
  const {
    title,
    href,
    children,
    icon: Icon,
    open: initialOpen = false,
    target,
    eventCode,
  } = props;
  const { account } = useAuthContext();
  const { mutate: trackUserEvent } = useTrackUserEvent();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(initialOpen);
  const buttonRef = useRef<HTMLDivElement>(null);

  const isCurrentPage = Boolean(href && location.pathname === href);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (children) {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          setIsExpanded(!isExpanded);
          break;
        case 'ArrowRight':
          if (!isExpanded) {
            event.preventDefault();
            setIsExpanded(true);
          }
          break;
        case 'ArrowLeft':
          if (isExpanded) {
            event.preventDefault();
            setIsExpanded(false);
          }
          break;
      }
    }
  };

  if (children) {
    return (
      <React.Fragment>
        <ListItem disablePadding>
          <ListItemButton
            ref={buttonRef}
            onClick={() => setIsExpanded(!isExpanded)}
            onKeyDown={handleKeyDown}
          >
            {Icon && (
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
            )}
            <ListItemText
              primary={title}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
                color: 'inherit',
              }}
            />
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </React.Fragment>
    );
  }

  if (href.startsWith('http') || target === '_blank') {
    return (
      <ListItem disablePadding>
        <Button
          variant="navigation"
          component="a"
          href={href}
          target="_blank"
          onClick={() => {
            if (eventCode) {
              trackUserEvent({
                slug: account,
                eventCode: 'c',
                payload: { on: eventCode },
              });
            }
          }}
          rel="noopener noreferrer"
          startIcon={Icon ? <Icon /> : null}
          sx={navigationButtonStyles}
        >
          {title}
        </Button>
      </ListItem>
    );
  }

  return (
    <ListItem disablePadding>
      <Button
        variant="navigation"
        component={Link}
        to={href}
        fullWidth
        startIcon={Icon ? <Icon /> : null}
        onClick={(e) => {
          e.currentTarget.blur();
          if (eventCode) {
            trackUserEvent({
              slug: account,
              eventCode: 'c',
              payload: { on: eventCode },
            });
          }
        }}
        sx={{
          ...navigationButtonStyles,
          fontWeight: isCurrentPage ? 600 : 400,
          borderLeft: isCurrentPage
            ? '3px solid var(--color-scifree-teal)'
            : '3px solid transparent',
        }}
      >
        {title}
      </Button>
    </ListItem>
  );
};

export default SidebarNavLinkItem;
